// src/pages/Admin/Inventaire/EditInventaireModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaUpload, FaImage, FaBoxOpen } from "react-icons/fa";
import { updateInventaire } from "../../../services/inventaireServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";

// Services upload local (sharp) pour inventaire
import {
  uploadInventairePhoto,
  toAbsoluteInventairePhotoUrl,
} from "../../../services/inventairePhotoServices";

const DEFAULT_PHOTO = "/img/no-image.jpg";

// Helper cache-busting
const withBust = (url, v) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

export default function EditInventaireModal({
  isOpen,
  onClose,
  refreshList,
  inventaire,
}) {
  const [formData, setFormData] = useState({
    categorie: "",
    nom: "",
    stock: "",
    note: "",
    photo_url: "",
  });

  // Enregistrement
  const [saving, setSaving] = useState(false);

  // Sous-modale upload
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // "webp" | "jpg" | "png"

  // Aperçu + cache-busting
  const [imgVersion, setImgVersion] = useState(0);

  const dialogRef = useRef(null);
  const formRef = useRef(null);

  // État initial dérivé de l’item courant
  const initial = useMemo(
    () => ({
      categorie: inventaire?.categorie || "",
      nom: inventaire?.nom || "",
      stock:
        inventaire?.stock === null || inventaire?.stock === undefined
          ? ""
          : String(inventaire?.stock),
      note: inventaire?.note || "",
      photo_url:
        inventaire?.photo_url && inventaire.photo_url.trim().length > 0
          ? inventaire.photo_url
          : "",
    }),
    [inventaire]
  );

  // Hydrate + reset cache-busting à l’ouverture/changement
  useEffect(() => {
    if (!isOpen || !inventaire) return;
    setFormData(initial);
    setImgVersion((v) => v + 1);
  }, [isOpen, inventaire, initial]);

  // Verrouille le scroll + ESC. Ferme d’abord la sous-modale, puis le parent.
  useEffect(() => {
    if (!isOpen) {
      // reset sous-modale upload à la fermeture
      setIsUploadOpen(false);
      setSelectedFile(null);
      setUploading(false);
      setUploadProgress(0);
      return;
    }
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (isUploadOpen) {
        setIsUploadOpen(false);
        return;
      }
      handleClose();
    };
    window.addEventListener("keydown", onEsc);

    // Focus titre (catégorie) au prochain tick
    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='categorie']")?.focus();
    }, 0);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, isUploadOpen]);

  // Dirty check simple
  const isDirty =
    formData.categorie !== initial.categorie ||
    formData.nom !== initial.nom ||
    String(formData.stock) !== String(initial.stock) ||
    (formData.note || "") !== (initial.note || "") ||
    (formData.photo_url || "") !== (initial.photo_url || "");

  const handleClose = () => {
    if (uploading || saving) return;
    if (!isDirty || window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")) {
      onClose?.();
    }
  };

  // Clique sur le fond : ne ferme PAS le parent si la sous-modale est ouverte
  const onBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (isUploadOpen) return;
    handleClose();
  };

  // Champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = name === "stock" ? value.replace(",", ".") : value;
    setFormData((prev) => ({ ...prev, [name]: next }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payloadBase = {
        categorie: (formData.categorie || "").trim(),
        nom: (formData.nom || "").trim(),
        stock:
          formData.stock === "" || formData.stock === null
            ? 0
            : Number.isNaN(Number(formData.stock))
            ? 0
            : Math.max(0, parseInt(Number(formData.stock), 10)),
        photo_url:
          formData.photo_url?.trim()
            ? formData.photo_url.trim()
            : DEFAULT_PHOTO,
      };

      const payload =
        (formData.note || "").trim().length > 0
          ? { ...payloadBase, note: formData.note.trim() }
          : { ...payloadBase, note: null };

      await updateInventaire(inventaire.id, payload);
      toast.success("Inventaire mis à jour avec succès");
      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'inventaire:", error);
      toast.error("Erreur lors de la mise à jour de l'inventaire");
    } finally {
      setSaving(false);
    }
  };

  // Upload image (local sharp) — sous-modale
  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] || null);

  const handleUpload = async (e) => {
    e.preventDefault(); // la sous-modale a son propre <form>

    if (!inventaire?.id) {
      toast.error("Objet introuvable — impossible d’associer l’image.");
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image.");
      return;
    }

    // Validation front
    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadInventairePhoto(inventaire.id, selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      setFormData((prev) => ({
        ...prev,
        photo_url: updated?.photo_url || prev.photo_url,
      }));
      setImgVersion((v) => v + 1);

      toast.success("Image téléversée et optimisée.");
      // Fermer la sous-modale sans fermer la modale parente
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Upload image inventaire:", err);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen || !inventaire) return null;

  const previewAbs = toAbsoluteInventairePhotoUrl(
    (formData.photo_url && formData.photo_url.trim()) ? formData.photo_url : DEFAULT_PHOTO
  );
  const preview = withBust(previewAbs, imgVersion);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier un article d’inventaire"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
              <FaBoxOpen />
            </span>
            <h3 className="text-white text-lg sm:text-xl font-bold uppercase">
              Modifier l’article d’inventaire
            </h3>
          </div>
          <button
            type="button"
            aria-label="Fermer"
            className="h-11 w-11 grid place-items-center rounded-xl bg-white/15 hover:bg-white/25 text-white"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose();
            }}
          >
            <FaTimes className="text-[18px]" />
          </button>
        </div>

        {/* ======= FORMULAIRE (corps + footer) ======= */}
        <form
          ref={formRef}
          id="edit-inventaire-form"
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 flex flex-col"
        >
          {/* Corps scrollable */}
          <div
            className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-5"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-5">
              {/* Colonne image */}
              <div className="flex flex-col gap-3">
                <div className="relative h-36 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden flex items-center justify-center">
                  <img
                    src={preview}
                    alt={formData.nom || "Objet inventaire"}
                    onError={(e) => {
                      e.currentTarget.src = DEFAULT_PHOTO;
                    }}
                    className="h-full w-full object-contain"
                  />
                  <span className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full grid place-items-center bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                    <FaImage className="text-xs" />
                  </span>
                </div>

                <Button
                  type="button"
                  text="Téléverser une image"
                  icon={FaUpload}
                  className="!rounded-xl gap-2"
                  onClick={(e) => {
                    // ⛔ ne pas soumettre le form parent
                    e.preventDefault();
                    e.stopPropagation();
                    setIsUploadOpen(true);
                  }}
                />

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Lien URL de la photo
                  </label>
                  {/* ⬇️ type="text" pour accepter aussi /uploads/... */}
                  <input
                    type="text"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    placeholder="https://… ou /uploads/… (rempli automatiquement après upload)"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    Astuce : utilisez <strong>“Téléverser une image”</strong> pour envoyer un visuel
                    (optimisation automatique ≤ 600&nbsp;px). Vous pouvez aussi coller une URL ou un
                    chemin local commençant par <code>/uploads/</code>.
                  </p>
                </div>
              </div>

              {/* Colonne formulaire */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Catégorie <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Nom <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Stock <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="stock"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={formData.stock}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Note (optionnelle)
                    </label>
                    <input
                      type="text"
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      placeholder="État, taille, accessoires, etc."
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Espace pour éviter que le contenu passe sous le footer */}
            <div className="h-2" />
          </div>

          {/* Footer sticky — DANS le form (soumission garantie) */}
          <div className="sticky bottom-0 z-10 px-5 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95">
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                text="Annuler"
                icon={FaTimes}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose();
                }}
              />
              {/* Utilise requestSubmit pour être 100% fiable avec un Button custom */}
              <Button
                type="button"
                text={saving ? "Enregistrement…" : "Enregistrer"}
                icon={FaSave}
                className="!rounded-xl gap-2"
                disabled={saving}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formRef.current?.requestSubmit();
                }}
              />
              {/* Fallback accessibilité */}
              <button type="submit" className="sr-only" aria-hidden="true" tabIndex={-1}>
                submit
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Sous-modale upload image (local) */}
      {isUploadOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => e.currentTarget === e.target && setIsUploadOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Téléverser une image d’inventaire"
          >
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-slate-900 dark:text-white font-semibold mb-3">
                Téléverser une image (optimisée ≤ 600px)
              </h4>

              <form onSubmit={handleUpload} className="space-y-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="block w-full text-sm text-slate-700 dark:text-slate-200"
                />

                <div className="flex items-center gap-2">
                  <span className="text-sm">Format</span>
                  <select
                    className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    disabled={uploading}
                  >
                    <option value="webp">WEBP (recommandé)</option>
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>

                {uploading && (
                  <div>
                    <progress className="progress w-full" value={uploadProgress} max="100" />
                    <div className="text-xs mt-1">{uploadProgress}%</div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    text="Fermer"
                    icon={FaTimes}
                    className="!rounded-xl"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsUploadOpen(false);
                    }}
                  />
                  <Button
                    type="submit"
                    text={uploading ? "Transfert…" : "Sauvegarder"}
                    icon={FaSave}
                    className="!rounded-xl"
                    disabled={uploading || !selectedFile}
                  />
                </div>
              </form>

              <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
                L’image sera redimensionnée côté serveur (largeur max 600&nbsp;px) et convertie
                au format choisi.
              </p>
            </div>
          </div>,
          document.body
        )}
    </div>,
    document.body
  );
}
